// ./games/crash/state/phases/PhaseManager.ts
import type { StompMessage } from '@core/Web/types/StompMessage';
import type { IPhase, PhaseContext } from './IPhase';

export class PhaseManager {
  private currentPhase: IPhase | null = null;
  private context: PhaseContext; // MODIFIED: Added properties to track timing data between transitions

  private lastPhaseTimingData: { phaseName: string; phaseEnd: number } | null =
    null;
  private lastPhaseExitTime: number | null = null;

  constructor(context: PhaseContext) {
    this.context = context;
  } // MODIFIED: Updated signature and added comprehensive logging

  public transitionTo(
    phase: IPhase,
    timingData?: { phase: string; phaseStart: number; phaseEnd: number }
  ): void {
    const clientTransitionTime = Date.now(); // --- Log Exit Details for the PREVIOUS Phase ---

    if (this.currentPhase) {
      if (this.lastPhaseTimingData) {
        const endDelta =
          clientTransitionTime - this.lastPhaseTimingData.phaseEnd;
        console.log(
          `[Phase][Metrics] Exiting Phase: ${this.lastPhaseTimingData.phaseName}`,
          {
            serverPhaseEnd: this.lastPhaseTimingData.phaseEnd,
            clientExitTime: clientTransitionTime,
            endDelta: `${endDelta}ms`,
          }
        );
      }
      this.currentPhase.onExit();
      this.lastPhaseExitTime = clientTransitionTime; // Record the exact exit time
    }

    this.currentPhase = phase;
    const newPhaseName = timingData?.phase || phase.constructor.name; // --- Log Enter Details for the NEW Phase ---

    if (timingData) {
      const startDelta = clientTransitionTime - timingData.phaseStart;
      const transitionDurationDelta = this.lastPhaseExitTime
        ? clientTransitionTime - this.lastPhaseExitTime
        : null;

      console.log(`[Phase][Metrics] Entering Phase: ${newPhaseName}`, {
        serverPhaseStart: timingData.phaseStart,
        clientEnterTime: clientTransitionTime,
        startDelta: `${startDelta}ms`,
        previousPhaseExitTime: this.lastPhaseExitTime,
        transitionDurationDelta: transitionDurationDelta
          ? `${transitionDurationDelta}ms`
          : 'N/A (first phase)',
        fullTimingData: timingData,
      }); // Store the current phase's data for the *next* transition's exit log

      this.lastPhaseTimingData = {
        phaseName: newPhaseName,
        phaseEnd: timingData.phaseEnd,
      };
    } else {
      // Handle cases where timingData is not provided (e.g., initial phase)
      console.log(
        `[Phase][Metrics] Entering Phase: ${newPhaseName} (No server timing data)`
      );
      this.lastPhaseTimingData = null; // Reset if no timing data
    }

    this.currentPhase.onEnter();
  }

  public handleMessage(message: StompMessage): void {
    if (this.currentPhase) {
      this.currentPhase.handleMessage(message);
    } else {
      console.warn(
        '[PhaseManager] No current phase to handle message:',
        message
      );
    }
  }

  public update(deltaTime: number): void {
    if (this.currentPhase) {
      this.currentPhase.update(deltaTime);
    }
  }
}
